import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { routes } from '@/lib/routes';
import { ArrowLeft } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { TeamChatWindow } from '@/tools/inner-team/components/chat/TeamChatWindow';
import { ChatInput } from '@/tools/inner-team/components/chat/ChatInput';
import { CharacterPortrait } from '@/tools/inner-team/components/team/CharacterPortrait';
import { useChat } from '@/hooks/useChat';
import { useChatStore } from '@/store/useChatStore';
import { useTeamStore } from '@/store/useTeamStore';
import { LoginPromptDialog } from '@/components/auth/LoginPromptDialog';
import { VoucherPromptDialog } from '@/components/auth/VoucherPromptDialog';
import type { ChatMessage } from '@/types';

export function TeamConsultChat() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const sessions = useChatStore((s) => s.sessions);
  const setActiveSession = useChatStore((s) => s.setActiveSession);
  const members = useTeamStore((s) => s.members);

  const session = sessions.find((s) => s.id === id);
  const sessionMembers = session
    ? members.filter((m) => session.memberIds.includes(m.id))
    : [];

  useEffect(() => {
    if (id) setActiveSession(id);
  }, [id, setActiveSession]);

  const { messages, isStreaming, sendMessage, loginRequired, dismissLoginPrompt, voucherRequired, dismissVoucherPrompt } = useChat(id ?? '', 'team-consult');

  if (!session || sessionMembers.length === 0) {
    navigate(routes.innerTeam.discovery(), { replace: true });
    return null;
  }

  const greeting: ChatMessage = {
    id: '__greeting__',
    role: 'assistant',
    content:
      'Willkommen zur inneren Teamsitzung. Dein Team ist versammelt und bereit zuzuhören.\n\n' +
      'Beschreibe die Herausforderung, Entscheidung oder Situation, mit der du dich gerade beschäftigst — ' +
      'und jedes Mitglied deines inneren Teams wird dir seine Perspektive mitteilen.',
    timestamp: new Date().toISOString(),
  };

  return (
    <AppShell>
      <div className="flex items-center gap-3 px-4 h-14 shrink-0 border-b border-border bg-background/80 backdrop-blur-sm">
        <button
          onClick={() => navigate(routes.innerTeam.discovery())}
          className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label="Zurück"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex items-center -space-x-1.5 shrink-0">
          {sessionMembers.slice(0, 3).map((m) => (
            <div key={m.id} className="rounded-full ring-2 ring-background">
              <CharacterPortrait
                emoji={m.avatarEmoji}
                color={m.avatarColor}
                avatarImageUrl={m.avatarImageUrl}
                size="xs"
              />
            </div>
          ))}
          {sessionMembers.length > 3 && (
            <div className="w-8 h-8 rounded-full ring-2 ring-background bg-muted flex items-center justify-center shrink-0">
              <span className="text-[10px] font-semibold text-muted-foreground">
                +{sessionMembers.length - 3}
              </span>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate">Team-Konsultation</p>
          <p className="text-xs text-muted-foreground truncate">
            {sessionMembers.map((m) => m.name).join(', ')}
          </p>
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <TeamChatWindow
          messages={messages}
          isStreaming={isStreaming}
          sessionMembers={sessionMembers}
          greeting={greeting}
        />
        <ChatInput onSend={sendMessage} disabled={isStreaming} />
      </div>
      <LoginPromptDialog open={loginRequired} onOpenChange={(open) => { if (!open) dismissLoginPrompt(); }} />
      <VoucherPromptDialog open={voucherRequired} onOpenChange={(open) => { if (!open) dismissVoucherPrompt(); }} />
    </AppShell>
  );
}
